import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { addDays, dayLabel } from '../dates';
import { firstName } from './format';
import { usePortalLoad } from './offline';
import { ChevronIcon } from './PortalIcons';
import { PortalVisit } from './PortalVisit';
import { LoadFailure, SavedNote, useWide } from './shared';

interface Item {
  id: number;
  date: string;
  start_time: string;
  end_time: string;
  minutes: number;
  locality: string;
  customer_name: string;
  status: string;
  is_lead: boolean;
  accepted: boolean;
  changed: boolean;
  pay: string | null;
}
/** A day the contractor has marked off: all of it, or some hours. */
interface DayOff {
  date: string;
  all_day: boolean;
  hours: string[];
}
interface Schedule {
  today: string;
  items: Item[];
  time_off?: DayOff[];
}

/** How far ahead the upcoming list looks. The server sends four weeks by default. */
const DAYS = 21;

type Run =
  | { kind: 'day'; date: string; items: Item[]; off?: DayOff }
  | { kind: 'free'; from: string; to: string }
  | { kind: 'off'; from: string; to: string };

/**
 * The coming days in order: each booked day with its visits, each stretch of whole days
 * off blocked out as one line, and each stretch of free days as one quiet line.
 */
function runs(today: string, items: Item[], off: DayOff[]): Run[] {
  const out: Run[] = [];
  for (let n = 0; n < DAYS; n++) {
    const date = addDays(today, n);
    const booked = items.filter((i) => i.date === date);
    const dayOff = off.find((o) => o.date === date);
    const last = out[out.length - 1];
    if (booked.length > 0 || (dayOff && !dayOff.all_day)) {
      out.push({ kind: 'day', date, items: booked, off: dayOff });
    } else {
      const kind = dayOff ? 'off' : 'free';
      if (last?.kind === kind) last.to = date;
      else out.push({ kind, from: date, to: date });
    }
  }
  return out;
}

const span = (from: string, to: string) =>
  from === to ? dayLabel(from) : `${dayLabel(from)} to ${dayLabel(to)}`;

const STATUS: Record<string, string> = {
  completed: 'Done',
  cancelled: 'Cancelled',
  missed: 'Missed',
};

/**
 * A visit in a list. On a phone it opens the visit's own screen (and Back returns here);
 * on a wide screen it shows the visit beside the list.
 */
function VisitCard({ v, selected }: { v: Item; selected?: boolean }) {
  const wide = useWide();
  const { pathname } = useLocation();
  return (
    <Link
      to={wide ? `?v=${v.id}` : `/visits/${v.id}`}
      state={wide ? undefined : { from: pathname }}
      replace={wide}
      className={`p-visit${selected ? ' is-selected' : ''}`}
      aria-current={selected ? 'true' : undefined}
    >
      <span className="p-visit-top">
        <strong>
          {v.start_time}–{v.end_time}
        </strong>
        {v.changed && <em className="p-badge">Changed</em>}
      </span>
      <span>
        {firstName(v.customer_name)} <span className="muted">— {v.locality}</span>
      </span>
      <span className="p-tags">
        {v.status === 'scheduled' && !v.accepted && <em className="p-flag">Needs confirming</em>}
        {v.status !== 'scheduled' && <em>{STATUS[v.status] ?? v.status}</em>}
      </span>
      <ChevronIcon />
    </Link>
  );
}

/** Today's visits, or the next seven days by day. Wide screens show the chosen visit alongside. */
export function PortalSchedule(props: { mode: 'today' | 'week' }) {
  const load = usePortalLoad<Schedule>('/api/portal/schedule');
  const wide = useWide();
  const [params] = useSearchParams();
  const { data } = load;
  const today = data?.today ?? '';
  const heading = props.mode === 'today' ? 'Today' : 'Upcoming';
  const subtitle = !today
    ? ''
    : props.mode === 'today'
      ? dayLabel(today)
      : `${dayLabel(today)} to ${dayLabel(addDays(today, DAYS - 1))}`;

  const head = (
    <>
      <h1>{heading}</h1>
      {subtitle && <p className="muted p-sub">{subtitle}</p>}
    </>
  );
  if (!data) {
    return (
      <>
        {head}
        {load.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <LoadFailure what={props.mode === 'today' ? 'today’s visits' : 'your week'} load={load} />
        )}
      </>
    );
  }

  const off = data.time_off ?? [];
  const offToday = off.find((o) => o.date === today);
  const upcoming = data.items.filter((i) => i.date >= today);
  const todays = upcoming.filter((i) => i.date === today);
  const next = upcoming.find((i) => i.date > today);
  const shown =
    props.mode === 'today' ? todays : upcoming.filter((i) => i.date <= addDays(today, DAYS - 1));
  // On a wide screen something is always open beside the list: the one asked for, else the first.
  const asked = Number(params.get('v'));
  const selected = wide
    ? (data.items.find((i) => i.id === asked) ?? shown[0] ?? next ?? null)
    : null;

  const list =
    props.mode === 'today' ? (
      <>
        {offToday && (
          <p className="p-off">
            <strong>
              {offToday.all_day ? 'You are off today' : `Time off ${offToday.hours.join(', ')}`}
            </strong>
            <span>
              {offToday.all_day
                ? 'You marked today as time off.'
                : 'Visits in those hours are with the office to reassign.'}
            </span>
          </p>
        )}
        {todays.length === 0 && (
          <>
            {!offToday?.all_day && (
              <p className="empty">
                {next
                  ? 'Nothing booked today.'
                  : 'Nothing booked today, and nothing coming up yet.'}{' '}
                <Link to="/offers">See available jobs</Link>
              </p>
            )}
            {next && (
              <>
                <h2 className="p-day">Your next visit: {dayLabel(next.date)}</h2>
                <div className="p-list">
                  <VisitCard v={next} selected={selected?.id === next.id} />
                </div>
              </>
            )}
          </>
        )}
        <div className="p-list">
          {todays.map((v) => (
            <VisitCard key={v.id} v={v} selected={selected?.id === v.id} />
          ))}
        </div>
      </>
    ) : (
      <>
        {runs(today, shown, off).map((r) =>
          r.kind === 'free' ? (
            <p key={r.from} className="p-free">
              {span(r.from, r.to)}: nothing booked
            </p>
          ) : r.kind === 'off' ? (
            <p key={r.from} className="p-off">
              <strong>{span(r.from, r.to)}</strong>
              <span>Time off</span>
            </p>
          ) : (
            <section key={r.date} aria-labelledby={`d-${r.date}`} className="p-daygroup">
              <h2 id={`d-${r.date}`} className="p-day">
                {dayLabel(r.date)}
                {r.date === today && <span className="p-today"> · today</span>}
              </h2>
              {r.off && (
                <p className="p-off p-off-part">
                  <strong>Time off {r.off.hours.join(', ')}</strong>
                </p>
              )}
              {r.items.length > 0 && (
                <div className="p-list">
                  {r.items.map((v) => (
                    <VisitCard key={v.id} v={v} selected={selected?.id === v.id} />
                  ))}
                </div>
              )}
            </section>
          ),
        )}
      </>
    );

  return (
    <>
      {head}
      <SavedNote load={load} />
      {wide ? (
        <div className="p-split">
          <div className="p-split-list">{list}</div>
          <div className="p-split-detail">
            {selected ? (
              <PortalVisit key={selected.id} id={selected.id} embedded />
            ) : (
              <p className="p-split-empty muted">Choose a visit to see it here.</p>
            )}
          </div>
        </div>
      ) : (
        list
      )}
    </>
  );
}
